import { Type } from '@angular/core';
import { MenuHeaderTitleImageComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/menu-header-title/menu-header-title-image/menu-header-title-image.component';
import { Section } from '../section/section';
import { Product } from '../../product/dto/product';
import { SectionRowViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import { ProductMenuHeaderComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/product-menu-header.component';
import type { SectionWithProducts } from '../section/section-with-products';
import { PrintMenu } from '../print-menu';
import { PrintHeaderLayoutType } from '../../enum/shared/print-header-layout-type.enum';
import { ColumnGrouping } from '../section/column-grouping';
import { ColumnUtils } from '../../../utils/column-utils';
import { FONT, FontStyleSheet } from '../font-style-sheets';
import { SectionColumnType } from '../../enum/shared/section-column-type';
import { SectionColumnProductInfoType } from '../../enum/shared/section-column-product-info-type';
import { SectionColumnViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';
import { WithinNameCell } from '../../enum/shared/within-name-cell';
import { SectionColumnConfigCannabinoidKey } from '../../enum/shared/section-column-config-cannabinoid-key';
import { SectionColumnConfigState } from '../../enum/shared/section-column-config-state';

export class JimmyPrintMenu extends PrintMenu {
  getPageTopMargin(): string {
    return '0.5in';
  }

  getPageBottomMargin(): string {
    return '0.5in';
  }

  getPageLeftMargin(): string {
    return '0.5in';
  }

  getPageRightMargin(): string {
    return '0.5in';
  }

  getThemeClass(): string {
    return 'jimmy-theme';
  }

  getHeaderType(): Type<ProductMenuHeaderComponent> {
    return MenuHeaderTitleImageComponent;
  }

  getPrintHeaderLayoutType(): PrintHeaderLayoutType {
    return this.menuOptions?.printHeaderLayout || PrintHeaderLayoutType.FirstPage;
  }

  getShowInlineLabels(): WithinNameCell {
    return WithinNameCell.TitleLine;
  }

  override getProductSubtitle(section: Section, product: Product, rowVm: SectionRowViewModel): string {
    // Visibility check
    const thcState = rowVm?.section?.columnConfig?.get(SectionColumnConfigCannabinoidKey.THC)?.defaultState;
    const cbdState = rowVm?.section?.columnConfig?.get(SectionColumnConfigCannabinoidKey.CBD)?.defaultState;
    const thcDataValue = rowVm?.section?.columnConfig?.get(SectionColumnConfigCannabinoidKey.THC)?.dataValue;
    const cbdDataValue = rowVm?.section?.columnConfig?.get(SectionColumnConfigCannabinoidKey.CBD)?.dataValue;
    const isThcEnabled = thcState === SectionColumnConfigState.Auto || thcState === SectionColumnConfigState.On;
    const isCbdEnabled = cbdState === SectionColumnConfigState.Auto || cbdState === SectionColumnConfigState.On;
    // Get values
    const thc = rowVm.getCannabinoid('THC', thcDataValue);
    const cbd = rowVm.getCannabinoid('CBD', cbdDataValue);
    // Show appropriate value(s)
    if (isThcEnabled && isCbdEnabled) {
      if (thc !== '-' && cbd !== '-') {
        return `THC ${thc} \xa0\xa0 CBD ${cbd}`;
      } else if (thc !== '-') {
        return `THC ${thc}`;
      } else if (cbd !== '-') {
        return `CBD ${cbd}`;
      }
    } else if (isThcEnabled) {
      if (thc !== '-') {
        return `THC ${thc}`;
      }
    } else if (isCbdEnabled) {
      if (cbd !== '-') {
        return `CBD ${cbd}`;
      }
    } else {
      return null;
    }
  }

  getProductWrapperStyling(section: SectionWithProducts, sectionRowViewModel: SectionRowViewModel, odd: boolean) {
    const rowStyling = super.getProductWrapperStyling(section, sectionRowViewModel, odd);
    rowStyling['border-top'] = `1px solid ${this.menuOptions.bodyTextColor}`;
    return rowStyling;
  }

  whatFontsAreNeededToRenderContent(): FontStyleSheet[] {
    return [FONT.EdsMarketNarrowSlant.Regular_400, FONT.FuturaPt.Bold_700, FONT.FuturaPt.ExtraBold_800];
  }

  override getAdobeFontFamilyCssUrl(): string | null {
    return 'https://use.typekit.net/als1vup.css';
  }

  override getColumnOrdering(): Map<SectionColumnType, number> {
    return new Map<SectionColumnType, number>([
      [SectionColumnProductInfoType.Badge, 0],
      [SectionColumnProductInfoType.ProductTitle, 1],
      [SectionColumnProductInfoType.Brand, 2],
      [SectionColumnProductInfoType.Strain, 3],
      [SectionColumnProductInfoType.StrainType, 4],
      [SectionColumnProductInfoType.QuantityAndSize, 5],
      [SectionColumnProductInfoType.Quantity, 6],
      [SectionColumnProductInfoType.Size, 7],
      [SectionColumnProductInfoType.VariantType, 8],
      [SectionColumnProductInfoType.Stock, 9],
      ...SectionColumnViewModel.getDefaultAdditionalDetailsColumnOrder(10),
      SectionColumnViewModel.getDefaultProductTagGroupColumnOrdering(11),
      ...SectionColumnViewModel.getDefaultCannabinoidColumnOrdering(12),
      ...SectionColumnViewModel.getDefaultTerpeneColumnOrdering(13),
      [SectionColumnProductInfoType.GradingResults, 14],
      [SectionColumnProductInfoType.VariantPrice, 15],
      [SectionColumnProductInfoType.VariantSecondaryPrice, 16]
    ]);
  }

  getLineModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }

  getGridModeColumnGroupings(sectionColumnViewModels: SectionColumnViewModel[]): ColumnGrouping[] {
    return ColumnUtils.oneBigGrouping();
  }
}
